import { Capacitor } from "@capacitor/core";
import { createTRPCProxyClient, httpLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type { AppRouter } from "../server";
import { fetchWithEarlyFetches } from "./earlyFetch";
import { withCatchUpChunkBeacon } from "@engine/catchUpFetch";
import { impersonationRef } from "./impersonationRef";
import { posthogLinkRequestHeaders } from "@client/utils/sentryPosthogLink";
import { loadAuthToken } from "./iphone/lib/auth";

export function getClientBaseUrl() {
  return Capacitor.isNativePlatform() ? getNativeBaseUrl() : "";
}

export function getClientAbsoluteBaseUrl() {
  return Capacitor.isNativePlatform()
    ? getNativeAbsoluteBaseUrl()
    : window.location.origin;
}

/**
 * Resolve the tRPC base URL for native Capacitor builds.
 *
 * When `capacitor.config.json` has `server.url` set (`cap sync` with
 * `CAP_SERVER_URL`, i.e. dev-against-a-dev-server), the WebView origin IS
 * the dev server URL — relative `/api/trpc/...` paths resolve correctly
 * and are reachable from the device. Returning `""` for that case
 * matches how the desktop browser tree works.
 *
 * For true bundled production builds (no `server.url`, WebView loads
 * `capacitor://localhost/index.html` from the local app bundle), there
 * is no usable origin to hang relative paths off — fall back to
 * `VITE_BASE_URL` baked at Vite build time, which points at the
 * deployed API host.
 */
function getNativeBaseUrl(): string {
  if (
    typeof window !== "undefined" &&
    window.location.protocol.startsWith("http")
  ) {
    return "";
  }

  return getBundledNativeBaseUrl();
}

function getNativeAbsoluteBaseUrl(): string {
  if (
    typeof window !== "undefined" &&
    window.location.protocol.startsWith("http")
  ) {
    return window.location.origin;
  }

  return getBundledNativeBaseUrl();
}

function getBundledNativeBaseUrl() {
  const baseUrl = import.meta.env.VITE_BASE_URL;

  if (!baseUrl) {
    throw new Error("VITE_BASE_URL is required for bundled native builds");
  }

  return baseUrl.replace(/\/$/, "");
}

export const trpcUrl = `${getClientBaseUrl()}/api/trpc`;

export function buildAuthHeaders(): Record<string, string> {
  return buildAuthHeadersForViewer(impersonationRef.current);
}

// Pin a request to an explicit viewer instead of the live impersonation ref:
// a load that snapshotted its viewer up front (Round.tsx) passes the snapshot
// so a mid-load impersonation switch can't make later requests of that load
// carry a different viewer than the rest of it. Deliberately a SEPARATE
// function (not an optional parameter on buildAuthHeaders): frameworks call
// `headers` callbacks with their own argument (tRPC's httpLink passes {op}),
// which an optional parameter would silently absorb as a viewer.
export function buildAuthHeadersForViewer(
  actingAs: string | null,
): Record<string, string> {
  const headers: Record<string, string> = {
    ...buildDeviceAuthHeaders(),
    // Recording identity, so a backend error from this request links to the
    // same PostHog recording that frontend errors do (sentryPosthogLink.ts).
    ...posthogLinkRequestHeaders(),
  };

  if (actingAs) {
    headers["x-acting-as-user-id"] = actingAs;
  }

  return headers;
}

// Auth headers WITHOUT the acting-as header: always the real signed-in user.
// For device-scoped background work (the video download engine) that must not
// inherit an active impersonation — otherwise its fetches are misattributed to
// the impersonated user and 403-fail for rounds outside their access.
export function buildDeviceAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  if (Capacitor.isNativePlatform()) {
    const token = loadAuthToken();

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }
  }

  return headers;
}

// React Query integration - use this in components with hooks
export const trpc = createTRPCReact<AppRouter>();

// Catch-up requests (identified by their runBoundedCatchUpFetch signal) also
// bump the sync-activity beacon per received body chunk, so the stall guard
// reads a slow-but-flowing gap download as progress. Everything else passes
// through the early-fetch wrapper unchanged.
const fetchWithEarlyFetchesAndCatchUpBeacon = withCatchUpChunkBeacon(
  fetchWithEarlyFetches,
);

// Vanilla client - use this for non-hook contexts (storage adapters, callbacks)
// All the vanilla clients fetch through the early-fetch-aware wrapper: a GET
// whose URL matches an in-flight shell early fetch adopts it instead of
// duplicating it, and the wrapper refuses to adopt when the request carries
// impersonation/bearer headers the early fetch didn't (see earlyFetch.ts).
export const trpcVanilla = createTRPCProxyClient<AppRouter>({
  links: [
    httpLink({
      url: trpcUrl,
      headers: buildAuthHeaders,
      fetch: fetchWithEarlyFetchesAndCatchUpBeacon,
    }),
  ],
});

export type TrpcVanillaClient = typeof trpcVanilla;

// A vanilla client whose every request carries ONE viewer, regardless of
// where the live impersonation ref moves afterwards. The round load creates
// one per load (see Round.tsx loadRoundData): its requests resolve over
// seconds, and a mid-load impersonation switch must not make later requests
// (deferred profiles, action fetches) return data anonymized for a different
// viewer than the one the load keys its caches under.
export function trpcVanillaForViewer(
  actingAs: string | null,
): TrpcVanillaClient {
  return createTRPCProxyClient<AppRouter>({
    links: [
      httpLink({
        url: trpcUrl,
        headers: () => buildAuthHeadersForViewer(actingAs),
        fetch: fetchWithEarlyFetchesAndCatchUpBeacon,
      }),
    ],
  });
}

// Vanilla client that always authenticates as the real signed-in user (no
// acting-as header), for device-scoped background work — see
// buildDeviceAuthHeaders.
export const trpcVanillaDevice = createTRPCProxyClient<AppRouter>({
  links: [
    httpLink({
      url: trpcUrl,
      headers: buildDeviceAuthHeaders,
      fetch: fetchWithEarlyFetchesAndCatchUpBeacon,
    }),
  ],
});
